/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2021 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import * as R from 'ramda';
import { createSelector } from 'reselect';
import { getRouteParam } from '@nebula/rover';

import {
  showColumnSettings,
  versionsReleaseNotes,
  showTimeline
} from '../../casing/data/feature.flags';
import getEnv from '../../casing/env';
import {
  groupPlugins,
  selectAllViewPlugins,
  selectEnabledViewPlugins,
  selectImsConfig
} from '../../common/selectors';
import { matchPluginRoute } from '../../common/util/match.plugin.route';

export const selectViewRouteParams = getRouteParam('view');

export const selectViewPluginCatalog = createSelector(
  selectEnabledViewPlugins,
  selectAllViewPlugins,
  (enabled, all) => R.pipe(
    R.without(enabled),
    groupPlugins
  )(all)
);

const matchRouteToPlugin = (route, plugins) => {
  const routeMatch = matchPluginRoute(route);
  return R.find(({ namespace }) => namespace === routeMatch, plugins);
};

export const selectRoutePlugin = createSelector(
  selectViewRouteParams,
  selectEnabledViewPlugins,
  matchRouteToPlugin
);

export const selectViewPluginsGrouped = createSelector(
  selectEnabledViewPlugins,
  groupPlugins
);

const selectDefaultPlugin = createSelector(
  selectEnabledViewPlugins,
  plugins =>
    R.find(R.propEq('namespace', 'event-list'), plugins) || R.head(plugins)
);

export const getSelectedViewNamespace = createSelector(
  selectRoutePlugin,
  selectDefaultPlugin,
  (routePlugin, defaultPlugin) => R.prop('namespace', routePlugin) || R.prop('namespace', defaultPlugin)
);

export const selectViewInitSettings = createSelector(
  selectImsConfig,
  showColumnSettings,
  versionsReleaseNotes,
  showTimeline,
  (imsConfig, canShowColumnSettings, showReleaseNotes, canShowTimeline) => ({
    ...imsConfig,
    env: getEnv(),
    showColumnSettings: canShowColumnSettings,
    showTimeline: canShowTimeline,
    showReleaseNotes
  })
);
